import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding, withEnabledBlockingInitialNavigation } from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideState, provideStore, Store } from '@ngrx/store';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { TenantTranslateLoader } from './services/translate/tenant-translate-loader';
import { filter, firstValueFrom, tap } from 'rxjs';
import { AuthService } from '@portal/auth';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { ChartService, MAP_SETTINGS, provideIconSettings, provideMapState, provideNotification } from '@ui/aos-lib';
import {
  AbstractSecurityStorage,
  OidcSecurityService,
  provideAuth,
  withAppInitializerAuthCheck
} from 'angular-auth-oidc-client';

import {
  loadData$ as consumptionAnalyticsLoadData$,
  refresh$ as consumptionAnalyticsRefresh$,
  triggerConsumptions$ as consumptionAnalyticsTriggerConsumptions$,
  triggerConsumptionReads$ as consumptionAnalyticsTriggerConsumptionReads$,
  onUnitOfMeasureChange$ as consumptionAnalyticsOnUnitOfMeasureChange$,
  loadWidgetConsumptions$ as consumptionAnalyticsLoadWidgetConsumptions$,
  loadConsumptions$ as consumptionAnalyticsLoadConsumptions$,
  loadConsumptionReads$ as consumptionAnalyticsLoadConsumptionReads$,
  getFiltersState$ as consumptionAnalyticsGetFiltersState$,
  getReportGroupAndInitFiltersState$ as consumptionAnalyticsGetReportGroupAndInitFiltersState$,
  filtersOrUomChange$ as consumptionAnalyticsFiltersOrUomChange$,
  saveFiltersState$ as consumptionAnalyticsSaveFiltersState$,
  loadConsumptionReadsElectric$ as consumptionAnalyticsLoadConsumptionReadsElectric$,
  loadConsumptionsElectric$ as consumptionAnalyticsLoadConsumptionsElectric$
} from './pages/csr/consumption-analytics/store/consumption-analytics.effects';
import { consumptionAnalyticsFeature } from './pages/csr/consumption-analytics/store/consumption-analytics.reducer';
import * as customerDetailsConsumptionEffects from './pages/csr/customers/components/customer-details-consumption/store/customer-details-consumption.effects';
import { customerDetailsConsumptionFeature } from './pages/csr/customers/components/customer-details-consumption/store/customer-details-consumption.reducer';
import { getEvents$ as customerDetailsEventsGetEvents$ } from './pages/csr/customers/components/customer-details-events/store/customer-details-events.effects';
import { customerDetailsEventsFeature } from './pages/csr/customers/components/customer-details-events/store/customer-details-events.reducer';
import { rmdEventsFeature } from './pages/equipment/devices/components/device-details/rmd-events/store/rmd-events.reducer';
import {
  getFiltersState$ as healthMonitorGetFiltersState$,
  getFiltersAndInitFiltersState$ as healthMonitorGetFiltersAndInitFiltersState$,
  loadDeviceTypesFilters$ as healthMonitorLoadDeviceTypesFilters$,
  loadHealthStatuses$ as healthMonitorLoadHealthStatuses$,
  loadReadRateStatistic$ as healthMonitorLoadReadRateStatistic$,
  loadReadSuccessRateStatistic$ as healthMonitorLoadReadSuccessRateStatistic$,
  loadHealthBatteryStatistic$ as healthMonitorLoadHealthBatteryStatistic$,
  loadHistoryLog$ as healthMonitorLoadHistoryLog$,
  loadAlarmStatistics$ as healthMonitorLoadAlarmStatistics$,
  saveFiltersState$ as healthMonitorSaveFiltersState$
} from './pages/equipment/health-monitor/store/health-monitor.effects';
import { healthMonitorFeature } from './pages/equipment/health-monitor/store/health-monitor.reducer';
import {
  getUserSettings$ as userSettingsGetUserSettings$,
  saveUserSettings$ as userSettingsSaveUserSettings$
} from './pages/help-and-settings/user-settings/store/user-settings.effects';
import { userSettingsFeature } from './pages/help-and-settings/user-settings/store/user-settings.reducer';
import {
  getMenuState$ as sideMenuStateGet$,
  saveMenuState$ as sideMenuStateSave$
} from './layout/components/side-menu/store/side-menu-state.effects';
import { sideMenuStateFeature } from './layout/components/side-menu/store/side-menu-state.reducer';

import { routesFactory } from './routes/app.routes';
import { PermissionsService } from './services/permissions/permissions.service';
import { MediaObserverService, provideCustomBreakpointsProvider } from './services/utility/media-observer.service';
import {
  loadOnDemandReads$,
  startOnDemandReadCalculations$
} from './shared/components/on-demand-read/store/on-demand-read.effects';
import { onDemandReadFeature } from './shared/components/on-demand-read/store/on-demand-read.reducer';
import * as valveStatusEffects from './shared/components/valve-status/store/valve-status.effects';
import { valveStatusFeature } from './shared/components/valve-status/store/valve-status.reducer';
import { BASE_URL } from './shared/constants/tokens';
import { authConfig } from './services/auth/auth-config';
import { MyStorageService } from './services/auth/my-local-storage.service';
import { devTools } from './store-devtools';
import { interceptors } from './interceptors';
import { mapReportGroupFeature } from './shared/components/map-report-group/store/map-report-group.reducer';
import { reportGroupCreated$ as mapReportGroupReportGroupCreated$ } from './shared/components/map-report-group/store/map-report-group.effects';
import { getEvents$ as rmdEventsGetEvents$ } from './pages/equipment/devices/components/device-details/rmd-events/store/rmd-events.effects';
import { listingFeature } from './shared/components/listing-page/store/listing-page.reducer';
import {
  gridDataFetched$ as listingPageGridDataFetched$,
  filterModelsFetched$ as listingPageFilterModelsFetched$,
  stateInitialized$ as listingPageStateInitialized$,
  exportTableData$ as listingPageExportTableData$,
  saveFiltersState$ as listingPageSaveFiltersState$
} from './shared/components/listing-page/store/listing-page.effects';
import { individualConsumptionTableFeature } from './pages/csr/components/individual-consumption-table/store/individual-consumption-table.reducer';
import {
  export$ as individualConsumptionTableExport$,
  fetchTableData$ as individualConsumptionTableFetchTableData$,
  fetchTableDataTriggered$ as individualConsumptionTableFetchTableDataTriggered$
} from './pages/csr/components/individual-consumption-table/store/individual-consumption-table.effects';

import { customerConsumptionFiltersFeature } from './pages/csr/customers/components/customer-consumption-filters/store/customer-consumption-filters.reducer';
import { deviceDetailsMetricsFeature } from './pages/equipment/devices/components/device-details/device-details-metrics/store/device-details-metrics.reducer';
import {
  getEvents$ as rmdMethaneLevelTabContentGetEvents$,
  requestEvents$ as rmdMethaneLevelTabContentRequestEvents$
} from './pages/equipment/devices/components/device-details/device-details-metrics/rmd-sensor/rmd-methane-level-tab-content/store/rmd-methane-level-tab-content.effects';
import { rmdMethaneLevelTabContentFeature } from './pages/equipment/devices/components/device-details/device-details-metrics/rmd-sensor/rmd-methane-level-tab-content/store/rmd-methane-level-tab-content.reducer';
import { meterConsumptionTabContentFeature } from './pages/equipment/devices/components/device-details/device-details-metrics/meter/meter-consumption-tab-content/store/meter-consumption-tab-content.reducer';
import {
  loadFilters$ as meterConsumptionTabContentLoadFilters$,
  loadFiltersOnMetricsTabSwitch$ as meterConsumptionTabContentLoadFiltersOnMetricsTabSwitch$,
  loadData$ as meterConsumptionTabContentLoadData$,
  loadChartData$ as meterConsumptionTabContentLoadChartData$,
  loadDataOnTabSwitch$ as meterConsumptionTabContentLoadDataOnTabSwitch$,
  loadTableData$ as meterConsumptionTabContentLoadTableData$,
  export$ as meterConsumptionTabContentExport$
} from './pages/equipment/devices/components/device-details/device-details-metrics/meter/meter-consumption-tab-content/store/meter-consumption-tab-content.effects';

import { meterMoreMetricsFeature } from './pages/equipment/devices/components/device-details/device-details-metrics/meter/meter-more-metrics-tab-content/store/meter-more-metrics.reducer';
import {
  loadOptions$ as meterMoreMetricsLoadOptions$,
  clearIfUnavailable$ as meterMoreMetricsClearIfUnavailable$
} from './pages/equipment/devices/components/device-details/device-details-metrics/meter/meter-more-metrics-tab-content/store/meter-more-metrics.effects';
import { meterElectricalTabContentFeature } from './pages/equipment/devices/components/device-details/device-details-metrics/meter/meter-electrical-tab-content/store/meter-electrical-tab-content.reducer';
import {
  loadFilters$ as meterElectricalTabContentLoadFilters$,
  loadFiltersOnMetricsTabSwitch$ as meterElectricalTabContentLoadFiltersOnMetricsTabSwitch$,
  loadData$ as meterElectricalTabContentLoadData$,
  loadDataOnTabSwitch$ as meterElectricalTabContentLoadDataOnTabSwitch$,
  loadChartData$ as meterElectricalTabContentLoadChartData$,
  loadTableData$ as meterElectricalTabContentLoadTableData$,
  reloadOnTableStateChange$ as meterElectricalTabContentReloadOnTableStateChange$,
  export$ as meterElectricalTabContentExport$
} from './pages/equipment/devices/components/device-details/device-details-metrics/meter/meter-electrical-tab-content/store/meter-electrical-tab-content.effects';
import { UserSettingsActions } from './pages/help-and-settings/user-settings/store/user-settings.actions';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routesFactory(), withEnabledBlockingInitialNavigation(), withComponentInputBinding()),
    provideHttpClient(withFetch(), withInterceptors(interceptors)),
    // Animations
    provideAnimations(),

    // BASE_URL token
    { provide: BASE_URL, useValue: location.origin },

    TenantTranslateLoader,
    provideTranslateService({
      loader: { provide: TranslateLoader, useExisting: TenantTranslateLoader },
      fallbackLang: 'en-us',
      lang: 'en-us'
    }),

    // Other providers (unchanged)
    provideIconSettings(),
    provideCustomBreakpointsProvider(),
    MediaObserverService,

    provideAuth(authConfig, withAppInitializerAuthCheck()),
    { provide: AbstractSecurityStorage, useClass: MyStorageService },
    ChartService,
    PermissionsService,
    AuthService,

    importProvidersFrom(NotificationModule),
    provideNotification(),

    // NgRx Store setup (unchanged)
    provideStore(),
    provideState(userSettingsFeature),
    provideState(healthMonitorFeature),
    provideState(consumptionAnalyticsFeature),
    provideState(onDemandReadFeature),
    provideState(customerDetailsConsumptionFeature),
    provideState(valveStatusFeature),
    provideState(customerDetailsEventsFeature),
    provideState(rmdEventsFeature),
    provideState(meterMoreMetricsFeature),
    provideState(mapReportGroupFeature),
    provideState(listingFeature),
    provideState(individualConsumptionTableFeature),
    provideState(customerConsumptionFiltersFeature),
    provideState(deviceDetailsMetricsFeature),
    provideState(rmdMethaneLevelTabContentFeature),
    provideState(meterConsumptionTabContentFeature),
    provideState(meterElectricalTabContentFeature),
    provideState(sideMenuStateFeature),
    ...devTools,
    provideEffects({
      getUserSettings$: userSettingsGetUserSettings$,
      saveUserSettings$: userSettingsSaveUserSettings$
    }),
    provideEffects({
      getMenuState$: sideMenuStateGet$,
      saveMenuState$: sideMenuStateSave$
    }),
    provideEffects({
      healthMonitorGetFiltersState$,
      getFiltersAndInitFiltersState$: healthMonitorGetFiltersAndInitFiltersState$,
      loadDeviceTypesFilters$: healthMonitorLoadDeviceTypesFilters$,
      loadHealthStatuses$: healthMonitorLoadHealthStatuses$,
      loadReadRateStatistic$: healthMonitorLoadReadRateStatistic$,
      loadReadSuccessRateStatistic$: healthMonitorLoadReadSuccessRateStatistic$,
      loadHealthBatteryStatistic$: healthMonitorLoadHealthBatteryStatistic$,
      loadHistoryLog$: healthMonitorLoadHistoryLog$,
      loadAlarmStatistics$: healthMonitorLoadAlarmStatistics$,
      saveFiltersState$: healthMonitorSaveFiltersState$
    }),
    provideEffects({
      loadData$: consumptionAnalyticsLoadData$,
      refresh$: consumptionAnalyticsRefresh$,
      triggerConsumptions$: consumptionAnalyticsTriggerConsumptions$,
      triggerConsumptionReads$: consumptionAnalyticsTriggerConsumptionReads$,
      onUnitOfMeasureChange$: consumptionAnalyticsOnUnitOfMeasureChange$,
      loadWidgetConsumptions$: consumptionAnalyticsLoadWidgetConsumptions$,
      loadConsumptions$: consumptionAnalyticsLoadConsumptions$,
      loadConsumptionReads$: consumptionAnalyticsLoadConsumptionReads$,
      getFiltersState$: consumptionAnalyticsGetFiltersState$,
      getReportGroupAndInitFiltersState$: consumptionAnalyticsGetReportGroupAndInitFiltersState$,
      filtersOrUomChange$: consumptionAnalyticsFiltersOrUomChange$,
      saveFiltersState$: consumptionAnalyticsSaveFiltersState$,
      consumptionAnalyticsLoadConsumptionReadsElectric$,
      consumptionAnalyticsLoadConsumptionsElectric$
    }),
    provideEffects({ startOnDemandReadCalculations$, loadOnDemandReads$ }),
    provideEffects(customerDetailsConsumptionEffects),
    provideEffects(valveStatusEffects),
    provideEffects({ getEvents$: customerDetailsEventsGetEvents$ }),
    provideEffects({ getEvents$: rmdEventsGetEvents$ }),
    provideEffects({
      requestEvents$: rmdMethaneLevelTabContentRequestEvents$,
      getEvents$: rmdMethaneLevelTabContentGetEvents$
    }),
    provideEffects({
      loadOptions$: meterMoreMetricsLoadOptions$,
      clearIfUnavailable$: meterMoreMetricsClearIfUnavailable$
    }),
    provideEffects({ reportGroupCreated$: mapReportGroupReportGroupCreated$ }),
    provideEffects({
      gridDataFetched$: listingPageGridDataFetched$,
      filterModelsFetched$: listingPageFilterModelsFetched$,
      stateInitialized$: listingPageStateInitialized$,
      exportTableData$: listingPageExportTableData$,
      saveFiltersState$: listingPageSaveFiltersState$
    }),
    provideEffects({
      fetchTableDataTriggered$: individualConsumptionTableFetchTableDataTriggered$,
      fetchTableData$: individualConsumptionTableFetchTableData$,
      export$: individualConsumptionTableExport$
    }),
    provideEffects({
      loadFilters$: meterConsumptionTabContentLoadFilters$,
      loadFiltersOnMetricsTabSwitch$: meterConsumptionTabContentLoadFiltersOnMetricsTabSwitch$,
      loadData$: meterConsumptionTabContentLoadData$,
      loadChartData$: meterConsumptionTabContentLoadChartData$,
      loadDataOnTabSwitch$: meterConsumptionTabContentLoadDataOnTabSwitch$,
      loadTableData$: meterConsumptionTabContentLoadTableData$,
      export$: meterConsumptionTabContentExport$
    }),
    provideEffects({
      loadFilters$: meterElectricalTabContentLoadFilters$,
      loadFiltersOnMetricsTabSwitch$: meterElectricalTabContentLoadFiltersOnMetricsTabSwitch$,
      loadData$: meterElectricalTabContentLoadData$,
      loadDataOnTabSwitch$: meterElectricalTabContentLoadDataOnTabSwitch$,
      loadChartData$: meterElectricalTabContentLoadChartData$,
      loadTableData$: meterElectricalTabContentLoadTableData$,
      reloadOnTableStateChange$: meterElectricalTabContentReloadOnTableStateChange$,
      export$: meterElectricalTabContentExport$
    }),
    // App initializer (unchanged)
    provideAppInitializer(() => {
      const initializerFn = (() => {
        const store = inject(Store);
        const authService = inject(OidcSecurityService);

        return (): void => {
          firstValueFrom(
            authService.isAuthenticated$.pipe(
              filter(({ isAuthenticated }) => isAuthenticated),
              tap(() => {
                store.dispatch(UserSettingsActions.getUserSettings());
                // TODO: move the permission request here as well
              })
            )
          );
        };
      })();
      return initializerFn();
    }),
    {
      provide: MAP_SETTINGS,
      useValue: { url: `${location.origin}/api/settings` }
    },
    provideMapState()
  ]
};
